/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';

import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { nativeIssueTableEntryPoint } from '@jira/issue-table__issue-table/src/ui/nativeIssueTableEntryPoint.entrypoint.tsx';

import type {
	JiraIssueSearchScope,
	JiraIssueSearchSettings,
	JiraTimelineViewSettings,
	JiraIssueSearchStaticViewInput,
	JiraIssueSearchAggregationConfigInput,
	JiraTimelineStaticViewInput,
	JiraTimelineViewRangeMode,
	JiraTimelineQuery,
} from '@jira/timeline__timeline-view/src/__generated__/JiraTimelineQuery.graphql';
import timelineParameters from '@jira/timeline__timeline-view/src/__generated__/JiraTimelineQuery$parameters';
import filterRefinementParameters from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery$parameters';
import type {
	FilterRefinementTimelineQuery,
	JiraJqlScopeInput,
} from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery.graphql.ts';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { fg } from '@atlassian/jira-feature-gating';
import type { TimelineEntrypointEntrypointProps } from './TimelineEntrypoint.entrypoint.types.tsx';
import businessTimelineParameters from './__generated__/JiraBusinessTimelineAppWithExperimentQuery$parameters.ts';

import type { JiraBusinessTimelineAppWithExperimentQuery } from './__generated__/JiraBusinessTimelineAppWithExperimentQuery.graphql.ts';

const TOGGLEABLE_FIELD_SET_IDS = ['assignee', 'status'];

const WEEKS = 'WEEKS' as const;
const MONTHS = 'MONTHS' as const;
const QUARTERS = 'QUARTERS' as const;

type AggregationConfig = {
	aggregationConfig: JiraIssueSearchAggregationConfigInput;
};

type Queries = {
	businessTimelineQuery: JiraBusinessTimelineAppWithExperimentQuery;
	timelineQuery: JiraTimelineQuery;
	filterRefinementQuery?: FilterRefinementTimelineQuery;
};

type ExtraProps = {
	today: number;
	isTimelineHarmonisationM2Enabled: boolean;
	isTimelineSavedViewEnabled: boolean;
	isTimelineGroupByEnabled: boolean;
	isBulkOperationsInTimelineEnabled: boolean;
	remountKey?: string;
	filterRefinementScope?: JiraJqlScopeInput;
	includeBoardFilters?: boolean;
	scrollElementHeightCalibration?: { scrollElementHeight: number; viewportHeightOverhead: number };
};

// Matches only plain objects: arrays, `null`, `Date`, `RegExp`, functions, and
// primitives are all excluded, so the `Record<string, unknown>` narrowing
// reflects the exact set of runtime values that reach the property access below.
const isRecord = (value: unknown): value is Record<string, unknown> =>
	Object.prototype.toString.call(value) === '[object Object]';

/**
 * Safely resolves the scroll element height calibration for a route from an
 * untrusted experiment value. Returns `undefined` (so the prop is omitted and
 * the Issue Table uses its built-in fallback) unless the value is a plain object
 * keyed by route name whose entry has finite numeric `scrollElementHeight` and
 * `viewportHeightOverhead`.
 */
const getScrollElementHeightCalibration = (
	calibration: unknown,
	routeName: string,
): { scrollElementHeight: number; viewportHeightOverhead: number } | undefined => {
	if (!isRecord(calibration)) {
		return undefined;
	}

	const entry = calibration[routeName];
	if (!isRecord(entry)) {
		return undefined;
	}

	const { scrollElementHeight, viewportHeightOverhead } = entry;
	if (
		typeof scrollElementHeight !== 'number' ||
		!Number.isFinite(scrollElementHeight) ||
		typeof viewportHeightOverhead !== 'number' ||
		!Number.isFinite(viewportHeightOverhead)
	) {
		return undefined;
	}

	return { scrollElementHeight, viewportHeightOverhead };
};

// Remove store after clean up fg timeline_harmonisation_m3_technical and jira_timeline_saved_view_biz
const store =
	typeof window !== 'undefined' ? createLocalStorageProvider('jwm-view-preferences') : undefined;

const and = (clauses: string[]): string => {
	return clauses
		.map((clause) => clause.trim())
		.filter((clause) => clause.length > 0)
		.join(' AND ');
};

const getProjectJql = (projectKey: string) =>
	projectKey.length > 0 ? `project = '${projectKey}'` : '';

const getSearchTextJql = (text?: string) => {
	if (text === undefined || text.trim() === '') {
		return '';
	}
	const sanitize = (value: string) =>
		value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\t/g, ' ');

	const issueKeyRegex = new RegExp('^([A-Z][A-Z0-9_]+-\\d+)$');
	const japaneseAndChineseRegex =
		/[\p{Script_Extensions=Hiragana}\p{Script_Extensions=Katakana}\p{Script_Extensions=Han}]/u;

	let search = sanitize(text);

	if (issueKeyRegex.test(text) && expVal('standalone-filter-refinement-sprt', 'isEnabled', false)) {
		return `(textfields ~ "${search}" OR issuekey = "${search}")`;
	}

	if (issueKeyRegex.test(text)) {
		return `(summary ~ '${search}*' OR description ~ '${search}*' OR key = '${search}')`;
	}

	// if the text does not contain Chinese or Japanese characters (which are not tokenized),
	// and does not end with a space character
	// make it a wildcard search
	if (!japaneseAndChineseRegex.test(text) && !text.endsWith(' ')) {
		search = `${search}*`;
	}

	if (expVal('standalone-filter-refinement-sprt', 'isEnabled', false)) {
		return `textfields ~ "${search}"`;
	}

	return `(summary ~ '${search}' OR description ~ '${search}')`;
};

const buildJql = (
	queryFilter = '',
	queryJql = '',
	storageFilter = '',
	text = '',
	projectKey = '',
) => {
	const filter =
		queryFilter ||
		(queryJql !== '' && expVal('standalone-filter-refinement-sprt', 'isEnabled', false)
			? queryJql
			: '') ||
		storageFilter;
	const search = getSearchTextJql(text);
	const project = getProjectJql(projectKey);

	return and([search, filter, project]);
};

function isValidTimelineMode(value: string): value is JiraTimelineViewRangeMode {
	return value === WEEKS || value === MONTHS || value === QUARTERS;
}

// should be equivalent to the getToday function in jira/src/packages/software/roadmap-utils/src/utils/dates.tsx
const getToday = (): number => {
	const now = new Date();
	const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
	return date.valueOf();
};

const parseQueryParamValue = (str?: string): boolean | undefined => {
	try {
		return str ? JSON.parse(str) : undefined;
	} catch {
		// Do nothing - ignore invalid values
	}
};

const toStaticViewInput = (
	settings: JiraIssueSearchSettings,
	aggregationConfig?: AggregationConfig,
): JiraIssueSearchStaticViewInput => {
	return {
		isGroupingEnabled: !!settings.groupBy,
		isHideDoneEnabled: settings.hideDone,
		isHierarchyEnabled: settings.hierarchyEnabled,
		...(aggregationConfig || { aggregationConfig }),
	};
};

const getFilterPreference = (entityId?: string, preferenceId = 'filter'): string => {
	if (
		fg('timeline_harmonisation_m3_technical') ||
		expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false)
	) {
		return '';
	}

	if (entityId === undefined) {
		return '';
	}

	const key = `${entityId}_timeline_${preferenceId}`;
	return store?.get(key);
};

const getHideDonePreference = (entityId?: string, preferenceId = 'hideDone'): true | undefined => {
	if (
		fg('timeline_harmonisation_m3_technical') ||
		expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false)
	) {
		return undefined;
	}

	if (entityId === undefined) {
		return undefined;
	}

	const key = `${entityId}_timeline_${preferenceId}`;
	const value = store?.get(key);

	// We only save the value in local storage if it's true, so we can ignore
	// anything else
	return value === 'true' || undefined;
};

const getProjectId = (projectKey: string): string => {
	if (
		fg('timeline_harmonisation_m3_technical') ||
		expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false)
	) {
		return '';
	}
	const key = 'timeline_project-mapping-preference';
	const projectMapping: Record<string, number> = store?.get(key) ?? {};
	const projectId: number | undefined = projectMapping[projectKey];
	return projectId?.toString() ?? '';
};

type TimelineEntrypointComponent = EntryPointComponent<
	Queries,
	{},
	TimelineEntrypointEntrypointProps,
	ExtraProps
>;

export const timelineEntrypoint: EntryPoint<TimelineEntrypointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<TimelineEntrypointComponent>(
			/* webpackChunkName: "async-harmonised-business-timeline" */ './JiraBusinessTimelineAppWithExperiment.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
			const {
				context,
				tenantContext: { cloudId },
			} = entryPointParams;
			const namespace = 'TIMELINE';
			const projectKey = context.match.params.projectKey;
			const {
				filter: queryFilter,
				hideDone,
				text,
				rangeMode,
				groupBy: queryGroupByParam,
				jql: queryJql,
			} = context.query || {};

			// Only treat the URL value as a groupBy override when the experiment is on
			// and the value is a non-empty string. When absent, leave settings.groupBy
			// null so the BE can apply its persisted groupByField.
			const queryGroupBy =
				typeof queryGroupByParam === 'string' &&
				queryGroupByParam !== '' &&
				expValEquals('jira_timeline_group_by', 'isEnabled', true, false)
					? queryGroupByParam
					: null;

			const normalizedRangeMode = rangeMode?.toUpperCase();
			const validatedRangeMode: JiraTimelineViewRangeMode | undefined =
				normalizedRangeMode && isValidTimelineMode(normalizedRangeMode)
					? normalizedRangeMode
					: undefined;

			const viewId = context.match.params.viewId;
			const itemIdSuffix = viewId ? `/${viewId}` : '';
			const itemId = expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false)
				? `timeline${itemIdSuffix}`
				: 'timeline';

			if (projectKey == null) {
				throw new Error('Project key missing');
			}

			const projectId = getProjectId(projectKey);
			const storageFilter = getFilterPreference(projectId);

			// The following is used to remount the timeline when the project key changes as to support the saved view feature.
			const projectKeyAndItemQuery = {
				cloudId,
				projectKey,
				itemId,
			};
			const jiraViewQueryInput = {
				projectKeyAndItemQuery,
			};
			const remountKey = JSON.stringify(projectKeyAndItemQuery);

			const storageHideDone = getHideDonePreference(projectId);

			const prevFieldSetIds = ['issuetype', 'issuekey', 'summary', 'timeline'];

			// Explicity set the aggregation config to false when the timeline_harmonisation_m2 experiment is disabled.
			const aggregationConfig = !expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
				? {
						aggregationConfig: {
							aggregationFields: [
								{
									fieldId: 'timeline',
									isEnabled: false,
								},
								{
									fieldId: 'status',
									isEnabled: false,
								},
							],
						},
					}
				: undefined;

			const { fieldSetIds, fieldSetsInput } = expValEquals(
				'timeline_harmonisation_m2',
				'isEnabled',
				true,
				false,
			)
				? {
						fieldSetIds: undefined,
						fieldSetsInput: {
							viewInput: {
								namespace: 'TIMELINE',
								viewId: 'timeline_default',
							},
						},
					}
				: {
						fieldSetIds: prevFieldSetIds,
						fieldSetsInput: {
							fieldSetIds: prevFieldSetIds,
						},
					};

			const scope: JiraIssueSearchScope = {
				operationScope: 'TIMELINE_PROJECT',
				projectKey,
			};

			// `viewScope` is the same value as `scope` above — it only exists so we can pass the
			// scope to the top-level jiraIssueSearchView behind jira_timeline_hierarchy_board_filter
			// without affecting the nested issues(scope), which must keep `scope` unconditionally.
			// On FG cleanup: delete `viewScope`, pass `scope` directly to jiraIssueSearchView.
			const viewScope: JiraIssueSearchScope | undefined = fg('jira_timeline_hierarchy_board_filter')
				? scope
				: undefined;

			// Combine with `scope` on `jira_timeline_group_by` FG cleanup
			// See https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/239157/diff#comment-2000357582
			const fieldSetsScope: JiraIssueSearchScope | undefined = expValEquals(
				'jira_timeline_group_by',
				'isEnabled',
				true,
				false,
			)
				? {
						operationScope: 'TIMELINE_PROJECT',
						projectKey,
					}
				: undefined;

			const hasFilterOverrides =
				queryFilter !== undefined ||
				text !== undefined ||
				(queryJql !== undefined && expVal('standalone-filter-refinement-sprt', 'isEnabled', false));
			const hasJqlOverrides =
				queryFilter !== undefined ||
				hideDone !== undefined ||
				text !== undefined ||
				validatedRangeMode !== undefined ||
				(queryJql !== undefined && expVal('standalone-filter-refinement-sprt', 'isEnabled', false));

			const jql = buildJql(queryFilter, queryJql, storageFilter, text, projectKey);

			const settings: JiraIssueSearchSettings = {
				...(queryGroupBy &&
					fg('jira_timeline_groupby_targeting') && {
						groupBy: queryGroupBy,
					}),
				// When the saved view or timeline M3 enabled, there’s no need to check hideDone from local storage.
				hideDone:
					expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false) ||
					fg('timeline_harmonisation_m3_technical')
						? parseQueryParamValue(hideDone)
						: (parseQueryParamValue(hideDone) ?? storageHideDone),
				hierarchyEnabled: true,
				jql: hasFilterOverrides ? jql : undefined,
			};

			const timelineViewSettings: JiraTimelineViewSettings = hasJqlOverrides
				? {
						hideDone: parseQueryParamValue(hideDone),
						jql,
						rangeMode: validatedRangeMode,
					}
				: {};

			const timelineStaticViewInput: JiraTimelineStaticViewInput =
				validatedRangeMode !== undefined
					? {
							rangeMode: validatedRangeMode,
						}
					: {};

			// JiraIssueSearchSettings will replace JiraIssueSearchStaticViewInput
			// as part of the saved views project, so we'll use it as the source of
			// truth
			const staticViewInput: JiraIssueSearchStaticViewInput = expValEquals(
				'jira_timeline_group_by',
				'isEnabled',
				true,
				false,
			)
				? // Without a URL override, mirror the no-override initial load: defer
					// grouping/hide-done to the BE saved config and only keep hierarchy
					// enabled. When the URL explicitly overrides groupBy/hideDone, forward
					// those so the override is honoured. Only safe when group-by is enabled
					// on the client, otherwise the BE can return a JiraGroupedTimelineView
					// that the client can't render/validate (isTimelineGroupByEnabled is false).
					{
						isHierarchyEnabled: true,
						...(queryGroupBy ? { isGroupingEnabled: true } : {}),
						...(hideDone ? { isHideDoneEnabled: !!parseQueryParamValue(hideDone) } : {}),
					}
				: toStaticViewInput(settings, aggregationConfig);

			const filterRefinementScope: JiraJqlScopeInput = {
				project: {
					projectKey,
				},
			};

			const dynamicSearchKey = `business-timeline-${projectKey}${viewId ? `-${viewId}` : ''}`;

			// Per-route scroll element height calibration is delivered via the
			// `issue_table_virtualizer_dynamic_estimators` experiment's `calibration`
			// parameter, a `Record<routeName, ScrollElementHeightCalibration>`. The
			// experiment value is untrusted (it comes from remote config), so it is
			// validated before use; an absent or malformed entry yields `undefined` and
			// the calibration is omitted so the Issue Table falls back to its default.
			const scrollElementHeightCalibration = expVal(
				'issue_table_virtualizer_dynamic_estimators',
				'isEnabled',
				false,
			)
				? getScrollElementHeightCalibration(
						expVal<unknown>('issue_table_virtualizer_dynamic_estimators', 'calibration', null),
						context.route.name,
					)
				: undefined;
			return {
				extraProps: {
					...(expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false)
						? { remountKey }
						: {}),
					today: getToday(),
					isTimelineSavedViewEnabled: expValEquals(
						'jira_timeline_saved_view_biz',
						'isEnabled',
						true,
						false,
					),
					isTimelineHarmonisationM2Enabled: expValEquals(
						'timeline_harmonisation_m2',
						'isEnabled',
						true,
						false,
					),
					isBulkOperationsInTimelineEnabled: expValEquals(
						'bulk_operations_in_timeline',
						'isEnabled',
						true,
						false,
					),
					isTimelineGroupByEnabled: expValEquals(
						'jira_timeline_group_by',
						'isEnabled',
						true,
						false,
					),
					...(fg('timeline-filter-refinement-technical-changes')
						? { filterRefinementScope, includeBoardFilters: false }
						: {}),
					...(scrollElementHeightCalibration ? { scrollElementHeightCalibration } : {}),
				},
				queries: {
					businessTimelineQuery: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						parameters: businessTimelineParameters,
						variables: {
							cloudId,
							projectKey,
						},
					},
					timelineQuery: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						parameters: timelineParameters,
						variables: {
							amountOfColumns: 500,
							cloudId,
							fieldSetIds,
							fieldSetsInput,
							filterId: null,
							first: expValEquals(
								'load_less_rows_on_timeline_harmonisation',
								'isEnabled',
								true,
								false,
							)
								? 30
								: 50,
							issueSearchInput: { jql },
							namespace,
							projectKey,
							settings,
							/*
										This key is used to generate a unique identity for the issue connection record in the Relay
										store when running a new search. This is to prevent concurrency related bugs occurring when
										performing a refetch that returns a new connection of results. Ordinarily this would be
										handled automatically when relevant query variables change (as these are used to form the
										connection key), however we override the default connection filters which allows us to modify
										variables during pagination operations without producing a new connection key.
										 */
							dynamicSearchKey,
							// Gated grouped-view fix — mirrors dynamicSearchKey (stable preload; uuid on
							// refetch). null = experiment off = original behaviour.
							// FG cleanup: delete this var, use $dynamicSearchKey on the grouped connection.
							groupByBugFixDynamicKey: expVal('timeline_group_by_bug_fixes', 'isEnabled', false)
								? dynamicSearchKey
								: null,
							...(expValEquals('jira_timeline_saved_view_biz', 'isEnabled', true, false) ||
							fg('timeline_harmonisation_m3_technical')
								? {
										timelineViewSettings,
										timelineStaticViewInput,
									}
								: {}),
							shouldQueryFieldSetsById: false,
							shouldQueryHasChildrenWithCount: true,
							shouldQueryHasParent: expValEquals(
								'business_timeline_hierarchy_flyout_sprt',
								'isEnabled',
								true,
								false,
							),
							scope,
							viewScope,
							fieldSetsScope,
							staticViewInput,
							viewConfigInput: { staticViewInput },
							jiraViewQueryInput,
							// cleanup with jira_timeline_saved_view_technical fg: replace with jiraViewQueryInput.
							jiraViewQueryInputForIssues: fg('jira_timeline_saved_view_technical')
								? jiraViewQueryInput
								: null,
							viewId: 'timeline_default',
							...(expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
								? {
										toggleableFieldSetIds: TOGGLEABLE_FIELD_SET_IDS,
										isAgentActivityColumnEnabled: expVal(
											'jira_timeline_agent_activity_column_fe',
											'isEnabled',
											false,
										),
										isInlineEditingEnabled: true,
										isTimelineHarmonisationM2Enabled: true,
									}
								: {
										isTimelineHarmonisationM2Enabled: false,
										isInlineEditingEnabled: false,
									}),

							isTimelineGroupByEnabled: expValEquals(
								'jira_timeline_group_by',
								'isEnabled',
								true,
								false,
							),
							...(fg('timeline_harmonisation_m3_technical')
								? {
										shouldQueryReleases: false,
										shouldQueryBarColor: false,
										shouldQuerySoftwareProjectSettings: false,
									}
								: {}),
						},
					},
					...(fg('timeline-filter-refinement-technical-changes')
						? {
								filterRefinementQuery: {
									options: {
										fetchPolicy: 'store-and-network' as const,
									},
									parameters: filterRefinementParameters,
									variables: {
										cloudId,
										filterRefinementScope,
										boardId: '',
										includeBoard: false,
									},
								},
							}
						: {}),
				},
				entryPoints: {
					nativeIssueTableEntryPoint: {
						entryPoint: nativeIssueTableEntryPoint,
						entryPointParams,
					},
				},
			};
		},
	});
